import type { MouseEvent, ReactElement } from 'react';
import { cloneElement, Fragment, memo, useMemo } from 'react';

import type { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';

import {
    AngleDownIcon,
    AngleUpIcon,
} from '@bestseller-bit/frontend-community.theme.buying-theme';
import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import Typography from '@mui/material/Typography';

import MenuItemSkeleton from './MenuItemSkeleton';
import { mofGroupName } from './utils';

const NoMaterialsFoundSubtitle = (): ReactElement => (
    <Typography
        color="textSecondary"
        variant="bodySmall.default"
        sx={{ display: 'block' }}
    >
        No materials found
    </Typography>
);

interface MaterialAutocompleteGroupProps extends AutocompleteRenderGroupParams {
    isCustomAllowed: boolean;
    isOnlyGroup: boolean;
    isEmptySubcategory: boolean;
    /** Whether this group can be expanded/collapsed by the user */
    isCollapsible: boolean;
    /** Whether this group is currently expanded */
    isExpanded: boolean;
    /** Whether this group is currently loading its data (e.g. full first page after expand) */
    isGroupLoading?: boolean;
    /** Whether search is active and no regular materials matched */
    hasNoMaterialSearchResults?: boolean;
    /** Toggles the expanded/collapsed state of this group */
    onToggleExpand: (headerElement: HTMLElement) => void;
}

const MaterialAutocompleteGroupRaw = ({
    isCustomAllowed,
    isOnlyGroup,
    isEmptySubcategory,
    isCollapsible,
    isExpanded,
    isGroupLoading = false,
    hasNoMaterialSearchResults = false,
    onToggleExpand,
    ...params
}: MaterialAutocompleteGroupProps): ReactElement => {
    /**
     * First we decide what type of group we are dealing with
     * If the group is empty, we consider it a non-value group
     * If the group is the MOF group, we treat it differently
     * If the group is not empty and not the MOF group, we treat it as a regular value group
     */
    const isNonValueGroup = params.group === '';
    const isMofGroup = params.group === mofGroupName;
    // We only show the list header if it's a regular group.
    const showListHeader = !isNonValueGroup && !isMofGroup;

    // Determine if the group is empty or not
    const hasNoOptions = useMemo(() => {
        if (Array.isArray(params.children)) {
            return params.children.every((child) => !child);
        } else {
            return !params.children;
        }
    }, [params.children]);

    /**
     * The header is interactive (clickable to expand/collapse) only when the group is
     * collapsible and not an empty subcategory. Empty subcategories have nothing to reveal,
     * so we hide the chevron and show the "No materials found" subtitle instead.
     */
    const isHeaderInteractive = isCollapsible && !isEmptySubcategory;

    const handleHeaderClick = (event: MouseEvent<HTMLElement>): void => {
        if (!isHeaderInteractive) return;
        // Prevent the click from selecting an option or closing the dropdown
        event.preventDefault();
        event.stopPropagation();
        onToggleExpand(event.currentTarget);
    };

    // Prevent the input from losing focus, which would close the dropdown
    const handleHeaderMouseDown = (event: MouseEvent<HTMLElement>): void => {
        if (!isHeaderInteractive) return;
        event.preventDefault();
    };

    return (
        <Fragment key={params.group}>
            {showListHeader && (
                <ListSubheader
                    sx={{
                        px: 3,
                        py: isEmptySubcategory ? 2 : 3,
                        lineHeight: 'normal',
                        ...(isHeaderInteractive && {
                            cursor: 'pointer',
                            ['&:hover']: {
                                backgroundColor: 'action.hover',
                            },
                        }),
                    }}
                    onMouseDown={handleHeaderMouseDown}
                    onClick={handleHeaderClick}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <Typography variant="buttons.small">
                            {params.group}
                        </Typography>
                        {isHeaderInteractive && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: ({ palette }) =>
                                        palette.text.primary,
                                }}
                            >
                                {isExpanded ? (
                                    <AngleUpIcon size={16} />
                                ) : (
                                    <AngleDownIcon size={16} />
                                )}
                            </Box>
                        )}
                    </Box>
                    {isEmptySubcategory && <NoMaterialsFoundSubtitle />}
                </ListSubheader>
            )}
            {/**
             * Ensure that each child has a unique key.
             * This part renders the options of the group OR the + MOF button.
             * Collapsed groups only contain a placeholder, so nothing is rendered for them.
             */}
            {Array.isArray(params.children)
                ? params.children.map((child, index) =>
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                      cloneElement(child, { key: child.key ?? index })
                  )
                : params.children}
            {/**
             * Show skeleton rows while the expanded group's full first page is being fetched.
             */}
            {isExpanded && isGroupLoading && (
                <>
                    <MenuItemSkeleton />
                    <MenuItemSkeleton />
                    <MenuItemSkeleton />
                </>
            )}
            {/**
             * When there are genuinely no materials at all and custom materials are not allowed,
             * the only group is the empty non-value group. We surface a "No materials found"
             * message there.
             */}
            {isOnlyGroup &&
                isNonValueGroup &&
                hasNoOptions &&
                !isCustomAllowed && (
                    <Box
                        sx={{
                            height: 44,
                            display: 'flex',
                            alignItems: 'center',
                            pl: 5,
                        }}
                    >
                        <NoMaterialsFoundSubtitle />
                    </Box>
                )}
            {/**
             * When searching returns no regular materials but MOF is allowed,
             * show "No materials found" below the MOF button so the user
             * knows there are no matching materials while still seeing MOF options.
             */}
            {hasNoMaterialSearchResults && isNonValueGroup && (
                <Box
                    sx={{
                        height: 44,
                        display: 'flex',
                        alignItems: 'center',
                        pl: 5,
                    }}
                >
                    <NoMaterialsFoundSubtitle />
                </Box>
            )}
        </Fragment>
    );
};

const MaterialAutocompleteGroup = memo(MaterialAutocompleteGroupRaw);

MaterialAutocompleteGroup.displayName = 'MaterialAutocompleteGroup';

export { MaterialAutocompleteGroup };
